import React, { useState, useEffect } from 'react';
import { TrendingUp, TrendingDown, AlertTriangle, BarChart3 } from 'lucide-react';
import { Card } from '../ui/Card';
import { useSupabaseAuth, useVulnerabilityReports } from '../../hooks/useSupabase';
import { formatTimestamp } from '../../utils/formatters';
import { getRiskLevel } from '../../utils/riskCalculations';
import type { VulnerabilityReport } from '../../types/database';

interface TrendData {
  date: string;
  jailbreakRisk: number;
  biasRisk: number;
  toxicityRisk: number;
  responseIntegrity: number;
  overallRisk: number;
}

export const TrendAnalysis: React.FC = () => {
  const { user } = useSupabaseAuth();
  const { reports, loading, getReports } = useVulnerabilityReports();
  const [trendData, setTrendData] = useState<TrendData[]>([]);
  const [selectedMetric, setSelectedMetric] = useState<keyof TrendData>('overallRisk');

  useEffect(() => {
    if (user && reports.length > 0) {
      const trends = reports
        .sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime())
        .map(report => ({
          date: report.created_at,
          jailbreakRisk: report.jailbreak_risk,
          biasRisk: report.bias_risk,
          toxicityRisk: report.toxicity_risk,
          responseIntegrity: report.response_integrity,
          overallRisk: report.overall_risk
        }));
      setTrendData(trends);
    }
  }, [user, reports]);

  const calculateTrend = (metric: keyof TrendData): { direction: 'up' | 'down' | 'stable'; change: number } => {
    if (trendData.length < 2) return { direction: 'stable', change: 0 };
    
    const recent = trendData.slice(-3);
    const older = trendData.slice(-6, -3);
    
    if (recent.length === 0 || older.length === 0) return { direction: 'stable', change: 0 };
    
    const recentAvg = recent.reduce((sum, item) => sum + item[metric], 0) / recent.length;
    const olderAvg = older.reduce((sum, item) => sum + item[metric], 0) / older.length;
    
    const change = recentAvg - olderAvg;
    const direction = Math.abs(change) < 5 ? 'stable' : change > 0 ? 'up' : 'down';
    
    return { direction, change: Math.abs(change) };
  };

  const getLatestScore = (metric: keyof TrendData): number => {
    if (trendData.length === 0) return 0;
    return trendData[trendData.length - 1][metric];
  };

  if (loading) {
    return (
      <Card variant="red">
        <div className="animate-pulse space-y-4">
          <div className="h-6 bg-red-100 rounded w-1/3"></div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {[1, 2, 3, 4].map(i => (
              <div key={i} className="h-24 bg-red-50 rounded"></div>
            ))}
          </div>
        </div>
      </Card>
    );
  }

  if (trendData.length === 0) {
    return (
      <Card variant="red">
        <div className="text-center py-8">
          <BarChart3 className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No Trend Data Available</h3>
          <p className="text-gray-600">
            Run multiple assessments to see vulnerability trends over time.
          </p>
        </div>
      </Card>
    );
  }

  const metrics = [
    { key: 'overallRisk' as const, label: 'Overall Risk', color: 'red' },
    { key: 'jailbreakRisk' as const, label: 'Jailbreak Risk', color: 'orange' },
    { key: 'biasRisk' as const, label: 'Bias Risk', color: 'yellow' },
    { key: 'toxicityRisk' as const, label: 'Toxicity Risk', color: 'red' },
    { key: 'responseIntegrity' as const, label: 'Response Integrity', color: 'green' }
  ];

  return (
    <div className="space-y-6">
      <Card variant="red">
        <h2 className="text-xl font-bold text-gray-900 mb-6 flex items-center">
          <BarChart3 className="h-5 w-5 mr-2" />
          Vulnerability Trends
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          {metrics.map((metric) => {
            const trend = calculateTrend(metric.key);
            const latestScore = getLatestScore(metric.key);
            const riskLevel = getRiskLevel(latestScore);
            
            return (
              <div
                key={metric.key}
                className={`p-4 rounded-lg border cursor-pointer transition-all ${
                  selectedMetric === metric.key 
                    ? 'border-red-500 bg-red-50' 
                    : 'border-red-500/30 hover:border-red-500/50'
                }`}
                onClick={() => setSelectedMetric(metric.key)}
              >
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-gray-900">{metric.label}</span>
                  {trend.direction === 'up' ? (
                    <TrendingUp className="h-4 w-4 text-red-500" />
                  ) : trend.direction === 'down' ? (
                    <TrendingDown className="h-4 w-4 text-green-500" />
                  ) : (
                    <div className="h-4 w-4 bg-gray-300 rounded-full" />
                  )}
                </div>
                <div className="text-2xl font-bold text-gray-900 mb-1">
                  {latestScore}
                </div>
                <div className="flex items-center justify-between">
                  <span className={`text-xs px-2 py-1 rounded-full ${
                    riskLevel === 'CRITICAL' ? 'bg-red-100 text-red-800' :
                    riskLevel === 'HIGH' ? 'bg-orange-100 text-orange-800' :
                    riskLevel === 'MEDIUM' ? 'bg-yellow-100 text-yellow-800' :
                    'bg-green-100 text-green-800'
                  }`}>
                    {riskLevel}
                  </span>
                  {trend.change > 0 && (
                    <span className={`text-xs ${
                      trend.direction === 'up' ? 'text-red-600' : 'text-green-600'
                    }`}>
                      {trend.direction === 'up' ? '+' : '-'}{trend.change.toFixed(1)}
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        <div className="border-t border-red-500/20 pt-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            {metrics.find(m => m.key === selectedMetric)?.label} Trend
          </h3>
          
          <div className="space-y-3">
            {trendData.slice(-10).map((data, index) => {
              const value = data[selectedMetric];
              const maxValue = Math.max(...trendData.map(d => d[selectedMetric]));
              const percentage = maxValue > 0 ? (value / maxValue) * 100 : 0;
              
              return (
                <div key={index} className="flex items-center space-x-3">
                  <div className="w-24 text-sm text-gray-600">
                    {formatTimestamp(data.date).split(' ')[0]}
                  </div>
                  <div className="flex-1 bg-gray-200 rounded-full h-2">
                    <div
                      className={`h-2 rounded-full ${
                        selectedMetric === 'responseIntegrity' 
                          ? 'bg-green-500' 
                          : 'bg-red-500'
                      }`}
                      style={{ width: `${percentage}%` }}
                    />
                  </div>
                  <div className="w-12 text-sm font-medium text-gray-900">
                    {value}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {trendData.length >= 3 && (
          <div className="mt-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
            <div className="flex items-start space-x-2">
              <AlertTriangle className="h-5 w-5 text-yellow-600 flex-shrink-0 mt-0.5" />
              <div>
                <h4 className="font-medium text-yellow-900">Trend Analysis</h4>
                <p className="text-sm text-yellow-800 mt-1">
                  Based on your recent assessments, we've detected some patterns in your AI model's security posture. 
                  Consider reviewing areas with increasing risk scores.
                </p>
              </div>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
};